import React, { useEffect, useState } from 'react';
import { RefreshCw, Plus, Edit, Trash2, Eye, X } from 'lucide-react';
import { API_URLS } from '../../../config/api';
import { adminApiFetch } from '../../../utils/adminApi';

export default function AdminMarkaTescilAdditionalServices() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    kurum_ucreti: '',
    servis_bedeli: '',
    required_documents: [],
    status: 'active'
  });
  const [newDocument, setNewDocument] = useState('');

  const fetchServices = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await adminApiFetch(API_URLS.ADMIN.MARKA_TESCIL.ADDITIONAL_SERVICES);
      const data = await res.json();
      const services = Array.isArray(data?.data) ? data.data : [];
      
      // Ensure required_documents is always an array
      const parsedServices = services.map(service => ({
        ...service,
        required_documents: Array.isArray(service.required_documents) ? service.required_documents : []
      }));
      
      setServices(parsedServices);
    } catch (e) {
      setError('Hizmetler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingService 
        ? API_URLS.ADMIN.MARKA_TESCIL.UPDATE_ADDITIONAL_SERVICE(editingService.id)
        : API_URLS.ADMIN.MARKA_TESCIL.CREATE_ADDITIONAL_SERVICE;
      
      const method = editingService ? 'PUT' : 'POST';
      
      const response = await adminApiFetch(url, {
        method,
        body: JSON.stringify(formData)
      });
      
      const result = await response.json();
      
      if (result.status === 'success') {
        setIsModalOpen(false);
        setEditingService(null);
        setFormData({
          title: '',
          description: '',
          kurum_ucreti: '',
          servis_bedeli: '',
          required_documents: [],
          status: 'active'
        });
        fetchServices();
      } else {
        setError(result.message || 'İşlem başarısız');
      }
    } catch (e) {
      setError('İşlem başarısız');
    }
  };

  const handleEdit = (service) => {
    setEditingService(service);
    setFormData({
      title: service.title,
      description: service.description,
      kurum_ucreti: service.kurum_ucreti,
      servis_bedeli: service.servis_bedeli,
      required_documents: service.required_documents || [],
      status: service.status
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('Bu hizmeti silmek istediğinizden emin misiniz?')) return;
    
    try {
      const response = await adminApiFetch(API_URLS.ADMIN.MARKA_TESCIL.DELETE_ADDITIONAL_SERVICE(id), {
        method: 'DELETE'
      });
      
      const result = await response.json();
      
      if (result.status === 'success') {
        fetchServices();
      } else {
        setError(result.message || 'Silme işlemi başarısız');
      }
    } catch (e) {
      setError('Silme işlemi başarısız');
    }
  };

  const toggleStatus = async (id, currentStatus) => {
    try {
      const response = await adminApiFetch(API_URLS.ADMIN.MARKA_TESCIL.TOGGLE_ADDITIONAL_SERVICE_STATUS(id), {
        method: 'PATCH'
      });
      
      const result = await response.json();
      
      if (result.status === 'success') {
        fetchServices();
      } else {
        setError(result.message || 'Durum güncellenemedi');
      }
    } catch (e) {
      setError('Durum güncellenemedi');
    }
  };

  const addDocument = () => {
    if (newDocument.trim()) {
      setFormData(prev => ({
        ...prev,
        required_documents: [...prev.required_documents, newDocument.trim()]
      }));
      setNewDocument('');
    }
  };

  const removeDocument = (index) => {
    setFormData(prev => ({
      ...prev,
      required_documents: prev.required_documents.filter((_, i) => i !== index)
    }));
  };

  useEffect(() => {
    fetchServices();
  }, []);

  return (
    <div className="px-2 sm:px-0">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0 mb-4 sm:mb-6">
        <h2 className="text-lg sm:text-xl font-bold">Marka Tescil Ek Hizmetleri</h2>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <button 
            onClick={() => {
              setEditingService(null);
              setFormData({
                title: '',
                description: '',
                kurum_ucreti: '',
                servis_bedeli: '',
                status: 'active'
              });
              setIsModalOpen(true);
            }}
            className="h-9 px-3 sm:px-4 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-xs sm:text-sm flex items-center gap-2 flex-1 sm:flex-initial justify-center"
          >
            <Plus className="w-4 h-4" /> <span className="hidden sm:inline">Yeni Hizmet</span>
          </button>
          <button onClick={fetchServices} className="h-9 px-3 rounded-md border border-gray-200 hover:bg-gray-50 text-xs sm:text-sm flex items-center gap-2 flex-1 sm:flex-initial justify-center">
            <RefreshCw className="w-4 h-4" /> <span className="hidden sm:inline">Yenile</span>
          </button>
        </div>
      </div>

      {error && <div className="mb-4 text-xs sm:text-sm text-red-600">{error}</div>}

      <div className="border border-gray-100 rounded-xl overflow-hidden">
        {/* Desktop Table Header */}
        <div className="hidden sm:grid grid-cols-12 bg-gray-50 text-xs sm:text-sm font-semibold text-gray-700 px-4 py-2">
          <div className="col-span-4">Hizmet Adı</div>
          <div className="col-span-3">Açıklama</div>
          <div className="col-span-2">Fiyat</div>
          <div className="col-span-2">Durum</div>
          <div className="col-span-1 text-right">İşlemler</div>
        </div>
        
        {loading ? (
          <div className="p-6 text-sm text-gray-500">Yükleniyor...</div>
        ) : services.length === 0 ? (
          <div className="p-6 text-sm text-gray-500">Hizmet bulunamadı.</div>
        ) : (
          <>
            {/* Desktop Table View */}
            <div className="hidden sm:block">
              {services.map((service, i) => (
                <div key={i} className="grid grid-cols-12 items-center px-4 py-3 border-t text-sm hover:bg-gray-50">
                  <div className="col-span-4 font-medium">{service.title}</div>
                  <div className="col-span-3 text-gray-600">{service.description}</div>
                  <div className="col-span-2">
                    <div className="text-xs text-gray-500">Kurum: ₺{parseFloat(service.kurum_ucreti || 0).toLocaleString('tr-TR', {minimumFractionDigits: 2})}</div>
                    <div className="text-xs text-gray-500">Servis: ₺{parseFloat(service.servis_bedeli || 0).toLocaleString('tr-TR', {minimumFractionDigits: 2})}</div>
                    <div className="text-sm font-semibold text-green-600">Toplam: ₺{(parseFloat(service.kurum_ucreti || 0) + parseFloat(service.servis_bedeli || 0)).toLocaleString('tr-TR', {minimumFractionDigits: 2})}</div>
                  </div>
                  <div className="col-span-2">
                    <button
                      onClick={() => toggleStatus(service.id, service.status)}
                      className={`px-2 py-1 rounded-full text-xs ${
                        service.status === 'active' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {service.status === 'active' ? 'Aktif' : 'Pasif'}
                    </button>
                  </div>
                  <div className="col-span-1 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button 
                        onClick={() => handleEdit(service)}
                        className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                        title="Düzenle"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={() => handleDelete(service.id)}
                        className="p-1 text-red-600 hover:bg-red-50 rounded"
                        title="Sil"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Mobile Card View */}
            <div className="sm:hidden space-y-3 p-3">
              {services.map((service, i) => (
                <div key={i} className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
                  <div>
                    <div className="font-semibold text-sm mb-1">{service.title}</div>
                    <div className="text-xs text-gray-600 line-clamp-2">{service.description}</div>
                  </div>
                  
                  <div className="space-y-1 text-xs">
                    <div className="text-gray-500">Kurum: ₺{parseFloat(service.kurum_ucreti || 0).toLocaleString('tr-TR', {minimumFractionDigits: 2})}</div>
                    <div className="text-gray-500">Servis: ₺{parseFloat(service.servis_bedeli || 0).toLocaleString('tr-TR', {minimumFractionDigits: 2})}</div>
                    <div className="font-semibold text-green-600">Toplam: ₺{(parseFloat(service.kurum_ucreti || 0) + parseFloat(service.servis_bedeli || 0)).toLocaleString('tr-TR', {minimumFractionDigits: 2})}</div>
                  </div>
                  
                  <div className="flex items-center justify-between pt-2 border-t">
                    <button
                      onClick={() => toggleStatus(service.id, service.status)}
                      className={`px-2 py-1 rounded-full text-xs ${
                        service.status === 'active' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {service.status === 'active' ? 'Aktif' : 'Pasif'}
                    </button>
                    <div className="flex flex-col items-end gap-1">
                      <button 
                        onClick={() => handleEdit(service)}
                        className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                        title="Düzenle"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={() => handleDelete(service.id)}
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                        title="Sil"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-4 sm:p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-base sm:text-lg font-semibold">
                {editingService ? 'Hizmet Düzenle' : 'Yeni Hizmet Ekle'}
              </h3>
              <button 
                onClick={() => setIsModalOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Hizmet Adı</label>
                <input 
                  type="text" 
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Açıklama</label>
                <textarea 
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows="3"
                  required
                />
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Kurum Ücreti (₺)</label>
                  <input 
                    type="number" 
                    step="0.01"
                    value={formData.kurum_ucreti}
                    onChange={(e) => setFormData(prev => ({ ...prev, kurum_ucreti: e.target.value }))}
                    className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Servis Bedeli (₺)</label>
                  <input 
                    type="number" 
                    step="0.01"
                    value={formData.servis_bedeli}
                    onChange={(e) => setFormData(prev => ({ ...prev, servis_bedeli: e.target.value }))}
                    className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">Gerekli Belgeler</label>
                <div className="space-y-2">
                  <div className="flex flex-col sm:flex-row gap-2">
                    <input 
                      type="text" 
                      value={newDocument}
                      onChange={(e) => setNewDocument(e.target.value)}
                      placeholder="Belge adı girin"
                      className="flex-1 px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      onKeyPress={(e) => e.key === 'Enter' && addDocument()}
                    />
                    <button 
                      type="button"
                      onClick={addDocument}
                      className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm sm:text-base"
                    >
                      Ekle
                    </button>
                  </div>
                  
                  {formData.required_documents.length > 0 && (
                    <div className="space-y-1">
                      {formData.required_documents.map((doc, index) => (
                        <div key={index} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded">
                          <span className="text-xs sm:text-sm break-words flex-1">{doc}</span>
                          <button 
                            type="button"
                            onClick={() => removeDocument(index)}
                            className="text-red-600 hover:text-red-800 ml-2 flex-shrink-0"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Durum</label>
                <select 
                  value={formData.status}
                  onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                  className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="active">Aktif</option>
                  <option value="inactive">Pasif</option>
                </select>
              </div>
              
              <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 pt-4">
                <button 
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 text-sm sm:text-base w-full sm:w-auto"
                >
                  İptal
                </button>
                <button 
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm sm:text-base w-full sm:w-auto"
                >
                  {editingService ? 'Güncelle' : 'Kaydet'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
